/**
 * My Unlocked Reports — permanent history of every report version the customer has
 * ever unlocked, newest first. Persists even when the live project is deleted.
 * Downloads use the existing project PDF/CSV endpoints (which serve the current live
 * project). Report versions carry the snapshot metadata for display.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { t, tr, useT, getLocale } from "@/lib/i18n";
import { Download, Trash2, RotateCw, FileText, FileSpreadsheet, CheckCircle2, Clock } from "lucide-react";
import { ReferralCard } from "@/components/ReferralCard";
import { RegenerateSnapshotModal } from "@/components/RegenerateSnapshotModal";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const downloadFileName = (v, ext, lang, isRegen) => {
  const safe = (v.project_name || "Report").replace(/\s+/g, "-").replace(/[^A-Za-z0-9-]/g, "");
  const date = (isRegen && v.regenerated_at ? v.regenerated_at : v.unlocked_at).slice(0, 10);
  const tag = isRegen && v.regenerated_at ? `Regenerated-${date}` : date;
  return `${safe}_Report-v${v.version}_${lang.toUpperCase()}_${tag}.${ext}`;
};

/** Shared download handler for PDF/CSV links on this page.
 *
 * The backend returns 410 Gone when the live project has been deleted AND we
 * no longer have a saved snapshot for the requested version. We intercept
 * that specific status to surface a friendly toast instead of the browser
 * dumping the raw JSON error page. Any other failure falls back to a plain
 * error toast so the user always gets feedback. */
const downloadWithFallback = async (e, url, filename, label) => {
  e.preventDefault();
  try {
    const r = await fetch(url, { credentials: "include" });
    if (r.status === 410) {
      const body = await r.json().catch(() => ({}));
      toast.error(body.detail || tr(`This ${label} needs to be regenerated. Use the Regenerate button in My Unlocked Reports.`, `Ce ${label} doit être régénéré. Utilisez le bouton Régénérer dans Mes rapports déverrouillés.`));
      return;
    }
    if (r.status === 503) {
      toast.error(t("pdf.unavailable"));
      return;
    }
    if (!r.ok) {
      toast.error(tr(`Could not download ${label} (HTTP ${r.status})`, `Impossible de télécharger le ${label} (HTTP ${r.status})`));
      return;
    }
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    toast.error(tr(`Could not download ${label}`, `Impossible de télécharger le ${label}`));
  }
};

export const UnlockedReports = () => {
  useT();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(true);
  const [regenModal, setRegenModal] = useState({ open: false, versionId: null });

  const load = () => api.get("/report-versions").then((r) => setVersions(r.data)).catch(() => toast.error(tr("Could not load reports", "Impossible de charger les rapports")));

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load().finally(() => setBusy(false));
  }, [user, loading, navigate]);

  const onDelete = async (v) => {
    const ok = window.confirm(tr(`Delete Report version ${v.version}?\n\nThis will remove all PDF, CSV, and language copies for this report version. This cannot be undone and does not restore a report credit.`, `Supprimer la version ${v.version} du rapport\u00a0?\n\nToutes les copies PDF, CSV et linguistiques de cette version seront supprimées. Cette action est irréversible et ne restitue pas de crédit de rapport.`));
    if (!ok) return;
    try {
      await api.delete(`/report-versions/${v.id}`);
      toast.success(tr(`Report version ${v.version} deleted — you can regenerate it for free anytime.`, `Version ${v.version} du rapport supprimée — vous pouvez la régénérer gratuitement à tout moment.`));
      load();
    } catch { toast.error(tr("Delete failed", "Échec de la suppression")); }
  };

  const onRegenerate = (v) => setRegenModal({ open: true, versionId: v.id });

  const onRegenerateDone = () => {
    // Refresh the list so the row picks up the new `regenerated_at` timestamp.
    load();
  };

  const onRequestLang = async (v, targetLang) => {
    try {
      await api.post(`/report-versions/${v.id}/request-translation`, { target_lang: targetLang });
      toast.success(getLocale() === "fr" ? `Version ${targetLang === "fr" ? "française" : "anglaise"} en préparation…` : `${targetLang === "fr" ? "French" : "English"} version preparing…`);
      load();
      // Poll for completion (background task flips to ready after 3s)
      setTimeout(load, 4000);
    } catch { toast.error(tr("Translation request failed", "Échec de la demande de traduction")); }
  };

  if (loading || busy) return (
    <div className="App"><Header /><div className="max-w-[1280px] mx-auto px-6 py-10">{tr("Loading…", "Chargement…")}</div><Footer /></div>
  );

  return (
    <div className="App">
      <Header />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-6">
          <div className="overline mb-2">{tr("Library", "Bibliothèque")}</div>
          <h1 className="font-display text-3xl md:text-4xl">{tr("My unlocked reports", "Mes rapports déverrouillés")}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>{tr("Every report you have ever unlocked, newest first. Reports remain here even if you delete the live project.", "Tous les rapports que vous avez déverrouillés, du plus récent au plus ancien. Ils restent ici même si vous supprimez le projet.")}</p>
        </div>

        {versions.length === 0 && (
          <div className="card p-8 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="reports-empty">
            {tr("You have not unlocked any reports yet.", "Vous n'avez encore déverrouillé aucun rapport.")} <button onClick={() => navigate("/projects")} className="link" style={{ color: "var(--brand-green)" }}>{tr("Go to My projects", "Allez dans Mes projets")}</button> {tr("to unlock your first report.", "pour déverrouiller votre premier rapport.")}
          </div>
        )}

        <div className="space-y-3">
          {versions.map((v) => <VersionRow key={v.id} v={v} onDelete={onDelete} onRegenerate={onRegenerate} onRequestLang={onRequestLang} />)}
        </div>

        {/* Referral CTA — shown after the report list so a happy buyer sees it right after downloading. */}
        {versions.length > 0 && (
          <div className="mt-10" data-testid="referral-card-slot-reports">
            <ReferralCard variant="reports" />
          </div>
        )}
      </div>
      <RegenerateSnapshotModal
        open={regenModal.open}
        versionId={regenModal.versionId}
        onClose={() => setRegenModal({ open: false, versionId: null })}
        onDone={onRegenerateDone}
      />
      <Footer />
    </div>
  );
};

const VersionRow = ({ v, onDelete, onRegenerate, onRequestLang }) => {
  const isDeleted = v.status === "deleted";
  const langs = v.languages || {};
  const availableLangs = ["en", "fr"].filter((L) => langs[L] === "ready");
  const preparingLangs = ["en", "fr"].filter((L) => langs[L] === "preparing");
  const missingLangs = ["en", "fr"].filter((L) => !langs[L] || langs[L] === null);
  return (
    <div className="card p-5" data-testid={`version-row-${v.id}`}>
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg">{v.project_name}</span>
            <span className="pill text-xs" style={{ background: "var(--paper)" }}>{tr("Report version", "Version du rapport")} {v.version}</span>
            {!isDeleted && <span className="pill text-xs" style={{ background: "#E4F3E7", color: "var(--brand-green)" }}><CheckCircle2 size={11} /> {v.regenerated_at ? tr("Regenerated", "Régénéré") : tr("Ready", "Prêt")}</span>}
            {isDeleted && <span className="pill text-xs" style={{ background: "#FCE7D8", color: "#873F00" }}>{tr("Deleted — regenerate free", "Supprimé — régénération gratuite")}</span>}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {(getLocale() === "fr" && v.country ? t(`country.${v.country}`, v.country) : v.country) || "—"} · {tr("Unlocked", "Déverrouillé le")} {v.unlocked_at?.slice(0, 10)}
            {v.regenerated_at && ` · ${tr("Regenerated", "Régénéré le")} ${v.regenerated_at.slice(0, 10)}`}
            {availableLangs.length > 0 && ` · ${tr("Available:", "Disponible\u00a0:")} ${availableLangs.map(l => l.toUpperCase()).join(" · ")}`}
            {preparingLangs.length > 0 && ` · ${tr("Preparing:", "En préparation\u00a0:")} ${preparingLangs.map(l => l.toUpperCase()).join(" · ")}`}
            {v.paid_via === "promo" && " · Promo"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isDeleted && availableLangs.map((L) => (
            <div key={L} className="inline-flex items-center gap-1.5">
              <a
                href={`${API_BASE}/api/projects/${v.project_id}/pdf?lang=${L}`}
                download={downloadFileName(v, "pdf", L, !!v.regenerated_at)}
                className="btn-secondary !py-1.5 !px-3 text-xs"
                data-testid={`vrow-pdf-${v.id}-${L}`}
                onClick={(e) => downloadWithFallback(e, `${API_BASE}/api/projects/${v.project_id}/pdf?lang=${L}`, downloadFileName(v, "pdf", L, !!v.regenerated_at), "PDF")}
              >
                <Download size={12} /> PDF ({L.toUpperCase()})
              </a>
              <a
                href={`${API_BASE}/api/projects/${v.project_id}/csv?lang=${L}`}
                download={downloadFileName(v, "csv", L, !!v.regenerated_at)}
                className="btn-secondary !py-1.5 !px-3 text-xs"
                data-testid={`vrow-csv-${v.id}-${L}`}
                onClick={(e) => downloadWithFallback(e, `${API_BASE}/api/projects/${v.project_id}/csv?lang=${L}`, downloadFileName(v, "csv", L, !!v.regenerated_at), "CSV")}
                title={tr(`Download CSV data (${L.toUpperCase()})`, `Télécharger les données CSV (${L.toUpperCase()})`)}
              >
                <FileSpreadsheet size={12} /> CSV
              </a>
            </div>
          ))}
          {!isDeleted && preparingLangs.map((L) => (
            <span key={`prep-${L}`} className="pill text-xs inline-flex items-center gap-1" style={{ background: "var(--paper)", color: "var(--muted)" }} data-testid={`vrow-preparing-${v.id}-${L}`}>
              <Clock size={11} /> {getLocale() === "fr" ? `Version ${L === "fr" ? "française" : "anglaise"} en préparation…` : `${L === "fr" ? "French" : "English"} version preparing…`}
            </span>
          ))}
          {!isDeleted && missingLangs.map((L) => (
            <button key={`get-${L}`} onClick={() => onRequestLang(v, L)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`vrow-getlang-${v.id}-${L}`}>
              {getLocale() === "fr" ? `Obtenir la version ${L === "fr" ? "française" : "anglaise"}` : `Get ${L === "fr" ? "French" : "English"} version`}
            </button>
          ))}
          {isDeleted && (
            <button onClick={() => onRegenerate(v)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`vrow-regen-${v.id}`}>
              <RotateCw size={12} /> {tr("Regenerate original", "Régénérer l'original")}
            </button>
          )}
          {!isDeleted && (
            <button onClick={() => onDelete(v)} className="text-xs px-2 py-1.5 rounded hover:bg-[var(--paper)]" data-testid={`vrow-delete-${v.id}`} title={tr("Delete report version", "Supprimer la version du rapport")}>
              <Trash2 size={12} style={{ color: "var(--muted)" }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnlockedReports;
